import type { ReactNode } from 'react';
import { IconAlertTriangle, IconCircleCheck, IconInfoCircle, IconAlertCircle } from '@tabler/icons-react';

export type CalloutTone = 'info' | 'warn' | 'bad' | 'ok';

export interface CalloutProps {
  tone?: CalloutTone;
  /** Bold first line. */
  title?: ReactNode;
  children?: ReactNode;
  /** Custom icon, or false for none. Defaults to an icon that matches the tone. */
  icon?: ReactNode | false;
  /** A button or link under the text ("Upload now"). */
  action?: ReactNode;
  /** Announce changes to screen readers (use for messages that appear after an action). */
  live?: boolean;
}

const ICONS: Record<CalloutTone, ReactNode> = {
  info: <IconInfoCircle size={20} />,
  warn: <IconAlertTriangle size={20} />,
  bad: <IconAlertCircle size={20} />,
  ok: <IconCircleCheck size={20} />,
};

/**
 * A short boxed message inside the page. Show at most one per screen section; never stack three.
 * Tones: info (teal), warn (amber), bad (red), ok (green); all AA in both schemes.
 */
export function Callout({ tone = 'info', title, children, icon, action, live }: CalloutProps) {
  const glyph = icon === false ? null : icon ?? ICONS[tone];
  return (
    <div
      className={`proc-callout ${tone}`}
      role={live ? (tone === 'bad' ? 'alert' : 'status') : undefined}
    >
      {glyph && <span className="ic" aria-hidden="true">{glyph}</span>}
      <div className="body">
        {title && <span className="title">{title}</span>}
        {children}
        {action && <div className="act">{action}</div>}
      </div>
    </div>
  );
}
